import React, { useState } from 'react';
import { usePresalesList } from '@/hooks/queries/presales/useList';
import { usePresalesCreate, usePresalesUpdate, usePresalesDelete } from '@/hooks/mutations/presales';
import { Pagination } from '@/components/Pagination';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog } from '@/components/AlertDialog';
import Loader from '@/components/Loader';
import { Plus, Edit2, Trash2, Tag, Search } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { useQuery } from '@tanstack/react-query';
import request from '@/services/api';
import { CourseSelect } from '@/components/CourseSelect';
import { CourseCell } from '@/components/CourseCell';
import { FileUploadInput } from '@/components/FileUploadInput';
import { getMediaUrl } from '@/utils/common';

interface Presale {
  id: string;
  courseId: string;
  courseTitle?: string | null;
  courseStatus?: string;
  coursePresalesEnabled?: boolean;
  discountType: 'PERCENT' | 'FIXED';
  discountValue: number;
  preSalesPrice?: number;
  startDate: string;
  endDate: string;
  limit?: number | null;
  counterOffset?: number;
  displayCount?: number;
  spotsLeft?: number | null;
  notifyWaitlist?: boolean;
  banner?: string;
  createdAt?: string;
}

interface PresaleInput {
  courseId: string;
  discountType: 'PERCENT' | 'FIXED';
  discountValue: number;
  startDate: string;
  endDate: string;
  limit?: number;
  counterOffset?: number;
  notifyWaitlist?: boolean;
  banner?: string;
}

const EMPTY_FORM: PresaleInput = {
  courseId: '',
  discountType: 'PERCENT',
  discountValue: 20,
  startDate: '',
  endDate: '',
  limit: 100,
  counterOffset: 0,
  notifyWaitlist: true,
  banner: '',
};

const PresalesPage = () => {
  const { toast } = useToast();
  const [listSearch, setListSearch] = useState('');
  const [listPage, setListPage] = useState(1);
  const [searchCourseId, setSearchCourseId] = useState('');
  const [queryCourseId, setQueryCourseId] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedPresale, setSelectedPresale] = useState<Presale | null>(null);
  const [formData, setFormData] = useState<PresaleInput>(EMPTY_FORM);

  const { data: listData, isLoading: listLoading, refetch: refetchList } = usePresalesList({
    pageNumber: listPage,
    pageSize: 20,
    search: listSearch.trim() || undefined,
    courseId: queryCourseId.trim() || undefined,
  });
  const listItems: Presale[] = listData?.data ?? [];

  const { data: presaleData, isLoading, refetch } = useQuery({
    queryKey: ['presales-by-course', queryCourseId],
    queryFn: async () => {
      if (!queryCourseId.trim()) return null;
      const res = await request.get(`/presales/${queryCourseId.trim()}`);
      return res.data?.data || res.data;
    },
    enabled: !!queryCourseId.trim(),
    retry: false,
  });

  const { mutate: createPresale, isPending: isCreating } = usePresalesCreate();
  const { mutate: updatePresale, isPending: isUpdating } = usePresalesUpdate();
  const { mutate: deletePresale } = usePresalesDelete();

  const handleSearch = () => {
    setQueryCourseId(searchCourseId);
  };

  const handleOpenCreate = () => {
    setSelectedPresale(null);
    setFormData({ ...EMPTY_FORM, courseId: queryCourseId || '' });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (presale: Presale) => {
    setSelectedPresale(presale);
    setFormData({
      courseId: presale.courseId,
      discountType: presale.discountType,
      discountValue: presale.discountValue,
      startDate: presale.startDate ? presale.startDate.slice(0, 10) : '',
      endDate: presale.endDate ? presale.endDate.slice(0, 10) : '',
      limit: presale.limit ?? 100,
      counterOffset: presale.counterOffset ?? 0,
      notifyWaitlist: presale.notifyWaitlist ?? true,
      banner: presale.banner || '',
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      ...formData,
      startDate: new Date(formData.startDate).toISOString(),
      endDate: new Date(formData.endDate).toISOString(),
    };

    if (selectedPresale) {
      updatePresale(
        { id: selectedPresale.courseId, data: payload },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Presale yangilandi' });
            setIsModalOpen(false);
            refetch();
            refetchList();
          },
          onError: () => toast({ title: 'Xatolik', description: 'Presale yangilanmadi', variant: 'destructive' }),
        }
      );
    } else {
      createPresale(payload, {
        onSuccess: () => {
          toast({ title: 'Muvaffaqiyatli', description: 'Yangi presale yaratildi. Kurs holati PRE_SALES ga o\'tkazildi.' });
          setIsModalOpen(false);
          setQueryCourseId(formData.courseId);
          refetch();
          refetchList();
        },
        onError: () => toast({ title: 'Xatolik', description: 'Presale yaratilmadi', variant: 'destructive' }),
      });
    }
  };

  const handleDelete = () => {
    if (!selectedPresale) return;
    deletePresale(selectedPresale.courseId, {
      onSuccess: () => {
        toast({
          title: 'Muvaffaqiyatli',
          description: "Presale o'chirildi. Agar kurs hali PRE_SALES holatida bo'lsa, Kurs tahririda PUBLISHED yoki DRAFT ga o'tkazing.",
        });
        setIsDeleteOpen(false);
        setSelectedPresale(null);
        setQueryCourseId('');
        setSearchCourseId('');
        refetchList();
      },
      onError: () => toast({ title: 'Xatolik', description: "Presale o'chirilmadi", variant: 'destructive' }),
    });
  };

  const presale: Presale | null = presaleData || null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Presales (Oldindan Sotuvlar)</h1>
          <p className="text-sm text-slate-500 mt-1">Kurslar uchun oldindan chegirmali sotuv kampaniyalarini boshqaring</p>
        </div>
        <Button onClick={handleOpenCreate} className="gap-2 bg-primary hover:opacity-90">
          <Plus className="size-4" /> Yangi presale
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Barcha presale kampaniyalar</p>
          <div className="flex gap-2 max-w-md w-full sm:w-auto">
            <Input
              placeholder="Kurs nomi bo'yicha qidirish..."
              value={listSearch}
              onChange={(e) => {
                setListSearch(e.target.value);
                setListPage(1);
              }}
              className="rounded-xl h-10"
            />
          </div>
        </div>
        {listLoading ? (
          <div className="py-10"><Loader /></div>
        ) : listItems.length === 0 ? (
          <div className="text-center py-12 text-slate-500 text-sm">Presale kampaniyalar topilmadi</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Kurs</th>
                  <th className="text-left px-4 py-3 font-semibold">Holat</th>
                  <th className="text-left px-4 py-3 font-semibold">Narx</th>
                  <th className="text-left px-4 py-3 font-semibold">Sotilgan</th>
                  <th className="text-left px-4 py-3 font-semibold">Qolgan</th>
                  <th className="text-right px-4 py-3 font-semibold">Amal</th>
                </tr>
              </thead>
              <tbody>
                {listItems.map((row) => (
                  <tr key={row.id || row.courseId} className="border-t border-slate-100 hover:bg-slate-50/80">
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-800">{row.courseTitle || '—'}</p>
                      <p className="text-xs text-slate-400 font-mono">{row.courseId.slice(0, 8)}…</p>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="text-xs">{row.courseStatus || '—'}</Badge>
                      {!row.coursePresalesEnabled && (
                        <p className="text-[10px] text-amber-600 mt-1">presalesEnabled: false</p>
                      )}
                    </td>
                    <td className="px-4 py-3">{row.preSalesPrice?.toLocaleString() ?? '—'} so&apos;m</td>
                    <td className="px-4 py-3">{row.displayCount ?? '—'}</td>
                    <td className="px-4 py-3">{row.spotsLeft ?? '∞'}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-lg text-xs"
                        onClick={() => {
                          setQueryCourseId(row.courseId);
                          setSearchCourseId(row.courseId);
                        }}
                      >
                        Ko&apos;rish
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination
          className="p-4 border-t border-slate-100"
          currentPage={listPage}
          setCurrentPage={setListPage}
          paginationInfo={listData?.meta?.pagination}
        />
      </div>

      {/* Search by courseId */}
      <div className="bg-white rounded-2xl border border-border shadow-sm p-5">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Kurs bo'yicha qidirish</p>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <CourseSelect
              value={searchCourseId}
              onChange={(id) => {
                setSearchCourseId(id);
                setQueryCourseId(id);
              }}
              allowEmpty={false}
              placeholder="Kursni tanlang..."
            />
          </div>
          <Button onClick={handleSearch} variant="outline" className="gap-2 rounded-xl shrink-0 h-10">
            <Search className="size-4" /> Tanlash
          </Button>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Presale har bir kursga alohida bog'langan. Kursni tanlang.
        </p>
      </div>

      {/* Result card */}
      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
        {!queryCourseId ? (
          <div className="text-center py-16 text-slate-500">
            <Tag className="size-12 mx-auto text-slate-300 mb-3" />
            <p className="font-medium">Kursni tanlang</p>
            <p className="text-sm text-slate-400 mt-1">Har bir kurs uchun bitta presale kampaniyasi bo'lishi mumkin</p>
          </div>
        ) : isLoading ? (
          <div className="py-12"><Loader /></div>
        ) : !presale ? (
          <div className="text-center py-16 text-slate-500">
            <Tag className="size-12 mx-auto text-slate-300 mb-3" />
            <p className="font-medium">Bu kurs uchun presale topilmadi</p>
            <div className="flex justify-center mt-2">
              <CourseCell courseId={queryCourseId} />
            </div>
            <Button onClick={handleOpenCreate} className="mt-4 gap-2 bg-primary hover:opacity-90 text-sm">
              <Plus className="size-4" /> Presale yaratish
            </Button>
          </div>
        ) : (
          <div className="p-6 space-y-5">
            {presale.banner && (
              <div className="rounded-xl overflow-hidden border border-slate-100 bg-slate-50 h-36">
                <img
                  src={getMediaUrl(presale.banner)}
                  alt="Presale banner"
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-slate-400 font-medium uppercase tracking-wide mb-1">Kurs</p>
                <CourseCell courseId={presale.courseId} />
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => handleOpenEdit(presale)} className="gap-1.5 rounded-xl text-xs">
                  <Edit2 className="size-3.5" /> Tahrirlash
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setSelectedPresale(presale); setIsDeleteOpen(true); }}
                  className="gap-1.5 rounded-xl text-xs text-red-500 border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="size-3.5" /> O'chirish
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-xs text-slate-500 font-medium mb-1">Chegirma</p>
                <Badge variant="outline" className="bg-blue-50 text-primary border-blue-200 text-sm font-bold">
                  {presale.discountType === 'PERCENT' ? `${presale.discountValue}%` : `${presale.discountValue?.toLocaleString()} so'm`}
                </Badge>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-xs text-slate-500 font-medium mb-1">Presale Narxi</p>
                <p className="text-sm font-bold text-slate-800">
                  {presale.preSalesPrice ? `${presale.preSalesPrice.toLocaleString()} so'm` : '—'}
                </p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-xs text-slate-500 font-medium mb-1">Boshlanish</p>
                <p className="text-xs font-semibold text-slate-700">{presale.startDate?.slice(0, 10) || '—'}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-xs text-slate-500 font-medium mb-1">Tugash</p>
                <p className="text-xs font-semibold text-slate-700">{presale.endDate?.slice(0, 10) || '—'}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 pt-2 border-t border-slate-100 text-xs text-slate-500">
              <span>Sotuv limiti: <strong className="text-slate-800">{presale.limit ? `${presale.limit} ta` : 'Cheksiz'}</strong></span>
              <span>•</span>
              <span>Waitlist bildirishnomasi: <strong className={presale.notifyWaitlist ? 'text-emerald-600' : 'text-slate-500'}>{presale.notifyWaitlist ? 'Yoqilgan' : 'O\'chirilgan'}</strong></span>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-xl bg-white rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {selectedPresale ? 'Presaleni tahrirlash' : 'Yangi presale kampaniyasi'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <CourseSelect
              label="Kurs"
              value={formData.courseId}
              onChange={(id) => setFormData({ ...formData, courseId: id })}
              required={true}
            />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Chegirma turi</Label>
                <Select
                  value={formData.discountType}
                  onValueChange={(val) => setFormData({ ...formData, discountType: val as 'PERCENT' | 'FIXED' })}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white rounded-xl shadow-lg border border-border">
                    <SelectItem value="PERCENT">Foiz (%)</SelectItem>
                    <SelectItem value="FIXED">Aniq summa (so'm)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Chegirma qiymati</Label>
                <Input
                  type="number"
                  value={formData.discountValue}
                  onChange={(e) => setFormData({ ...formData, discountValue: Number(e.target.value) })}
                  className="rounded-xl"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Boshlanish sanasi</Label>
                <Input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="rounded-xl"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Tugash sanasi</Label>
                <Input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="rounded-xl"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Maksimal joy (Limit)</Label>
                <Input
                  type="number"
                  value={Number(formData.limit) || 100}
                  onChange={(e) => setFormData({ ...formData, limit: Number(e.target.value) })}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Counter Offset (Fake ro'yxat)</Label>
                <Input
                  type="number"
                  value={Number(formData.counterOffset) || 0}
                  onChange={(e) => setFormData({ ...formData, counterOffset: Number(e.target.value) })}
                  className="rounded-xl"
                />
              </div>
            </div>

            <FileUploadInput
              label="Banner rasm (Ixtiyoriy)"
              value={formData.banner || ''}
              onChange={(url) => setFormData({ ...formData, banner: url })}
              accept="image/*"
              placeholder="Banner rasmini tanlang"
              disabled={isCreating || isUpdating}
            />

            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
              <div>
                <Label className="text-sm font-semibold text-slate-800">Kutish ro'yxatiga bildirishnoma</Label>
                <p className="text-xs text-slate-500">Kurs boshlanganda kutayotganlarga xabar borishi</p>
              </div>
              <Switch
                checked={formData.notifyWaitlist}
                onCheckedChange={(checked) => setFormData({ ...formData, notifyWaitlist: checked })}
              />
            </div>

            <DialogFooter className="pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl">
                Bekor qilish
              </Button>
              <Button type="submit" disabled={isCreating || isUpdating} className="rounded-xl bg-primary text-white">
                {isCreating || isUpdating ? 'Saqlanmoqda...' : 'Saqlash'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        alertTitle="Presaleni o'chirishni tasdiqlaysizmi?"
        alertDescription="Presale sozlamasi o'chiriladi va kursda presalesEnabled o'chiriladi. Kurs statusi PRE_SALES bo'lib qolishi mumkin — keyin Kurs tahririda PUBLISHED yoki DRAFT tanlang."
        alertActionTitle="O'chirish"
        alertCancel="Bekor qilish"
        alertActionFunction={handleDelete}
      />
    </div>
  );
};

export default PresalesPage;
